import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/hooks/use-toast';

export interface AwardDraft {
  id?: string;
  prizeName: string;
  year: number;
  shortlistDate: string;
  region: string;
  genreFocus: string;
  description: string;
  link: string;
  logoUrl: string;
  shortlistBooks: any[];
  enrichmentStatus: Record<string, any>;
}

export function useAwardDrafts() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const saveDraft = async (draft: AwardDraft) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('award_drafts')
        .upsert({
          user_id: user.id,
          prize_name: draft.prizeName,
          year: draft.year,
          shortlist_date: draft.shortlistDate || null,
          region: draft.region,
          genre_focus: draft.genreFocus,
          description: draft.description,
          link: draft.link,
          logo_url: draft.logoUrl,
          draft_data: {
            shortlistBooks: draft.shortlistBooks,
          },
          enrichment_status: draft.enrichmentStatus,
        }, {
          onConflict: 'user_id,prize_name,year'
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  };

  const loadDraft = async (prizeName: string, year: number) => {
    if (!user) return null;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('award_drafts')
        .select('*')
        .eq('user_id', user.id)
        .eq('prize_name', prizeName)
        .eq('year', year)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        return {
          id: data.id,
          prizeName: data.prize_name,
          year: data.year,
          shortlistDate: data.shortlist_date || '',
          region: data.region || 'UK',
          genreFocus: data.genre_focus || 'fiction',
          description: data.description || '',
          link: data.link || '',
          logoUrl: data.logo_url || '',
          shortlistBooks: (data.draft_data as any)?.shortlistBooks || [],
          enrichmentStatus: data.enrichment_status as Record<string, any> || {},
        } as AwardDraft;
      }
      
      return null;
    } catch (error) {
      console.error('Error loading draft:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteDraft = async (prizeName: string, year: number) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('award_drafts')
        .delete()
        .eq('user_id', user.id)
        .eq('prize_name', prizeName)
        .eq('year', year);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  };

  const listDrafts = async () => {
    if (!user) return [];
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('award_drafts')
        .select('prize_name, year, updated_at')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error listing drafts:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  return {
    saveDraft,
    loadDraft,
    deleteDraft,
    listDrafts,
    loading,
  };
}
